"use client";

import { m as motion, useInView, useReducedMotion } from "framer-motion";
import { useRef } from "react";

/**
 * "Settimana tipo": i giorni Lun-Ven si "consegnano" in sequenza mentre un
 * cursore ciano scorre. Loop; reduced-motion / fuori schermo -> statico
 * (settimana completa, così resta chiara anche senza animazione). Solo
 * transform/opacity. Companion della sezione "Una settimana tipo" (ProcessDemo).
 */

const CYCLE = 5; // durata loop settimana (s)

export function WeekVisual({ isItalian }: { isItalian: boolean }) {
  const reduce = useReducedMotion();
  const ref = useRef<HTMLDivElement>(null);
  const inView = useInView(ref, { margin: "-60px" });
  const run = inView && !reduce;

  const days = isItalian
    ? ["Lun", "Mar", "Mer", "Gio", "Ven"]
    : ["Mon", "Tue", "Wed", "Thu", "Fri"];
  const tasksPerDay = [2, 1, 2, 1, 2];

  return (
    <div
      ref={ref}
      aria-hidden
      className="pointer-events-none w-full max-w-[24rem] select-none"
    >
      <div className="rounded-2xl border border-[hsl(var(--border))] bg-surface p-6 shadow-[0_0_36px_hsl(var(--primary)/0.10)] md:p-7">
        <div className="mb-6 flex items-center justify-between">
          <span className="font-mono text-xs uppercase tracking-[0.14em] text-primary">
            {isItalian ? "Settimana tipo" : "Typical week"}
          </span>
          <span className="font-mono text-xs text-muted-foreground">
            {isItalian ? "Lun–Ven" : "Mon–Fri"}
          </span>
        </div>

        <div className="relative grid grid-cols-5 gap-2.5 pb-1">
          {run && (
            <motion.span
              className="absolute -top-1.5 bottom-6 w-[2px] rounded-full bg-primary shadow-[0_0_12px_hsl(var(--primary))]"
              initial={{ left: "8%" }}
              animate={{ left: ["8%", "28%", "48%", "68%", "88%"] }}
              transition={{
                duration: CYCLE,
                ease: "linear",
                repeat: Infinity,
                times: [0, 0.25, 0.5, 0.75, 1],
              }}
            />
          )}

          {days.map((d, di) => {
            const t = Math.max(0.02, di / days.length);
            return (
              <div key={d + di} className="flex flex-col items-center gap-2">
                {Array.from({ length: tasksPerDay[di] }).map((_, ti) => (
                  <span
                    key={ti}
                    className="relative h-3 w-full overflow-hidden rounded-full bg-foreground/12"
                  >
                    <motion.span
                      className="absolute inset-0 rounded-full bg-primary"
                      initial={{ opacity: run ? 0 : 1 }}
                      animate={run ? { opacity: [0, 0, 1, 1] } : { opacity: 1 }}
                      transition={
                        run
                          ? {
                              duration: CYCLE,
                              times: [0, t, Math.min(0.98, t + 0.05), 1],
                              repeat: Infinity,
                            }
                          : undefined
                      }
                    />
                  </span>
                ))}
                <span className="mt-1.5 font-mono text-[0.65rem] uppercase tracking-[0.06em] text-muted-foreground">
                  {d}
                </span>
              </div>
            );
          })}
        </div>

        <div className="mt-6 flex items-center justify-between border-t border-[hsl(var(--border))] pt-4">
          <span className="text-sm text-muted-foreground">
            {isItalian ? "Consegnati" : "Delivered"}
          </span>
          <span className="font-mono text-base font-semibold text-primary">
            {isItalian ? "8 task / sett." : "8 tasks / wk"}
          </span>
        </div>
      </div>
    </div>
  );
}
